// Base API Request Handler
// Auto-detect: Production uses same domain /api, Development uses localhost
const API_BASE_URL = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) 
  || (typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
      ? `${window.location.protocol}//${window.location.host}` 
      : '');

export interface RequestOptions extends RequestInit {
  timeout?: number;
}

/**
 * Get authentication token from storage
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem('telegram_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
};

export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { timeout = 30000, headers = {}, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    const token = getAuthToken();

    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...headers,
      },
    });

    clearTimeout(timeoutId);

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return { success: true } as T;
    }

    const data = await response.json();

    // For PIN verification, return the data even if response is not OK
    // Let the caller decide how to handle it
    if (!response.ok) {
      // Return error data for better error handling
      if (data && (data.message || data.error)) {
        return data; // Return error response as-is
      }
      throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error: any) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }

    throw error;
  }
}

export default apiRequest;
